import React from 'react';
import { motion } from 'framer-motion';
import Icon from '../../../components/AppIcon';

import { Checkbox } from '../../../components/ui/Checkbox';

const FilterPanel = ({
  activeTab,
  filters,
  setFilters,
  showFavoritesOnly,
  setShowFavoritesOnly,
  showRecentOnly,
  setShowRecentOnly,
  resultCount,
  onClearFilters
}) => {
  const muscleGroupOptions = [
    'Chest', 'Back', 'Shoulders', 'Arms', 'Legs', 'Core', 'Glutes', 'Calves'
  ];

  const equipmentOptions = [
    'Dumbbells', 'Barbell', 'Kettlebell', 'Resistance Bands', 'Pull-up Bar', 
    'Bench', 'Cable Machine', 'Bodyweight', 'Medicine Ball', 'TRX'
  ];

  const goalOptions = [
    'strength', 'hypertrophy', 'endurance', 'weight-loss', 'flexibility', 
    'athletic-performance', 'rehabilitation', 'general-fitness'
  ];

  const handleMuscleGroupToggle = (muscle) => {
    setFilters(prev => ({
      ...prev,
      muscleGroups: prev?.muscleGroups?.includes(muscle)
        ? prev?.muscleGroups?.filter(m => m !== muscle)
        : [...prev?.muscleGroups, muscle]
    }));
  };

  const handleEquipmentToggle = (equipment) => {
    setFilters(prev => ({
      ...prev,
      equipment: prev?.equipment?.includes(equipment)
        ? prev?.equipment?.filter(e => e !== equipment)
        : [...prev?.equipment, equipment]
    }));
  };

  return (
    <motion.div
      initial={{ opacity: 0, x: -20 }}
      animate={{ opacity: 1, x: 0 }}
      className="bg-card border border-border rounded-xl p-6 sticky top-24 max-h-[calc(100vh-120px)] overflow-y-auto"
    >
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-lg font-semibold text-foreground flex items-center gap-2">
          <Icon name="SlidersHorizontal" size={20} />
          Filters
        </h3>
        <button
          onClick={onClearFilters}
          className="text-sm text-primary hover:text-primary/80 transition-colors"
        >
          Clear All
        </button>
      </div>
      {/* Result Count */}
      <div className="mb-6 p-3 bg-primary/10 rounded-lg">
        <p className="text-sm text-foreground">
          <span className="font-bold text-primary">{resultCount}</span> results found
        </p>
      </div>
      {/* Quick Filters */}
      <div className="space-y-3 mb-6 pb-6 border-b border-border">
        <button
          onClick={() => setShowFavoritesOnly(!showFavoritesOnly)}
          className={`w-full flex items-center gap-3 p-3 rounded-lg transition-colors ${
            showFavoritesOnly 
              ? 'bg-primary text-primary-foreground' 
              : 'bg-muted hover:bg-muted/80 text-foreground'
          }`}
        >
          <Icon name="Star" size={18} />
          <span className="text-sm font-medium">Favorites Only</span>
        </button>
        {activeTab === 'plans' && (
          <button
            onClick={() => setShowRecentOnly(!showRecentOnly)}
            className={`w-full flex items-center gap-3 p-3 rounded-lg transition-colors ${
              showRecentOnly 
                ? 'bg-primary text-primary-foreground' 
                : 'bg-muted hover:bg-muted/80 text-foreground'
            }`}
          >
            <Icon name="Clock" size={18} />
            <span className="text-sm font-medium">Recently Used</span>
          </button>
        )}
      </div>
      {/* Category Filter (Exercises Only) */}
      {activeTab === 'exercises' && (
        <div className="mb-6 pb-6 border-b border-border">
          <label className="block text-sm font-medium text-foreground mb-3">
            Category
          </label>
          <select
            value={filters?.category}
            onChange={(e) => setFilters(prev => ({ ...prev, category: e?.target?.value }))}
            className="w-full px-3 py-2 rounded-lg border border-border bg-background text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary"
          >
            <option value="">All Categories</option>
            <option value="strength">Strength</option>
            <option value="cardio">Cardio</option>
            <option value="flexibility">Flexibility</option>
            <option value="balance">Balance</option>
            <option value="sports">Sports</option>
          </select>
        </div>
      )}
      {/* Difficulty Filter */}
      <div className="mb-6 pb-6 border-b border-border">
        <label className="block text-sm font-medium text-foreground mb-3">
          Difficulty Level
        </label>
        <div className="space-y-2">
          {['beginner', 'intermediate', 'advanced']?.map((level) => (
            <button
              key={level}
              onClick={() => setFilters(prev => ({ 
                ...prev, 
                difficulty: prev?.difficulty === level ? '' : level 
              }))}
              className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-colors ${
                filters?.difficulty === level
                  ? 'bg-primary text-primary-foreground'
                  : 'bg-muted hover:bg-muted/80 text-foreground'
              }`}
            >
              {level?.charAt(0)?.toUpperCase() + level?.slice(1)}
            </button>
          ))}
        </div>
      </div>
      {/* Duration Filter (Plans Only) */}
      {activeTab === 'plans' && (
        <div className="mb-6 pb-6 border-b border-border">
          <label className="block text-sm font-medium text-foreground mb-3">
            Duration (minutes)
          </label>
          <div className="space-y-2">
            {[
              { label: '15-30 min', value: '15-30' },
              { label: '30-45 min', value: '30-45' },
              { label: '45-60 min', value: '45-60' },
              { label: '60-90 min', value: '60-90' },
              { label: '90+ min', value: '90-999' }
            ]?.map((duration) => (
              <button
                key={duration?.value}
                onClick={() => setFilters(prev => ({ 
                  ...prev, 
                  duration: prev?.duration === duration?.value ? '' : duration?.value 
                }))}
                className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-colors ${
                  filters?.duration === duration?.value
                    ? 'bg-primary text-primary-foreground'
                    : 'bg-muted hover:bg-muted/80 text-foreground'
                }`}
              >
                {duration?.label}
              </button>
            ))}
          </div>
        </div>
      )}
      {/* Goal Filter (Plans Only) */}
      {activeTab === 'plans' && (
        <div className="mb-6 pb-6 border-b border-border">
          <label className="block text-sm font-medium text-foreground mb-3">
            Fitness Goal
          </label>
          <select
            value={filters?.goal}
            onChange={(e) => setFilters(prev => ({ ...prev, goal: e?.target?.value }))}
            className="w-full px-3 py-2 rounded-lg border border-border bg-background text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary"
          >
            <option value="">All Goals</option>
            {goalOptions?.map((goal) => (
              <option key={goal} value={goal}>
                {goal?.split('-')?.map(w => w?.charAt(0)?.toUpperCase() + w?.slice(1))?.join(' ')}
              </option>
            ))}
          </select>
        </div>
      )}
      {/* Muscle Groups Filter (Exercises Only) */}
      {activeTab === 'exercises' && (
        <div className="mb-6 pb-6 border-b border-border">
          <label className="block text-sm font-medium text-foreground mb-3">
            Target Muscle Groups
          </label>
          <div className="space-y-2 max-h-48 overflow-y-auto">
            {muscleGroupOptions?.map((muscle) => (
              <label key={muscle} className="flex items-center gap-2 cursor-pointer">
                <Checkbox
                  checked={filters?.muscleGroups?.includes(muscle)}
                  onChange={() => handleMuscleGroupToggle(muscle)}
                />
                <span className="text-sm text-foreground">{muscle}</span>
              </label>
            ))}
          </div>
        </div>
      )}
      {/* Equipment Filter (Exercises Only) */}
      {activeTab === 'exercises' && (
        <div className="mb-6">
          <label className="block text-sm font-medium text-foreground mb-3">
            Equipment Available
          </label>
          <div className="space-y-2 max-h-48 overflow-y-auto">
            {equipmentOptions?.map((equipment) => (
              <label key={equipment} className="flex items-center gap-2 cursor-pointer">
                <Checkbox
                  checked={filters?.equipment?.includes(equipment)}
                  onChange={() => handleEquipmentToggle(equipment)}
                />
                <span className="text-sm text-foreground">{equipment}</span>
              </label>
            ))}
          </div>
        </div>
      )}
    </motion.div>
  );
};

export default FilterPanel;